import maplibregl, { type ExpressionSpecification, type Map as MlMap, type StyleSpecification } from 'maplibre-gl'
import { onScopeDispose, type Ref } from 'vue'
import type { LineString, Point } from 'geojson'
import type { BBox, Feature, FeatureCollection, LinkProperties, NodeProperties } from '@/api/types'
import {
  FUNCTIONAL_DARK,
  FUNCTIONAL_LIGHT,
  NODE_A_DARK,
  NODE_A_LIGHT,
  NODE_B_DARK,
  NODE_B_LIGHT,
  NO_DATA_DARK,
  NO_DATA_LIGHT,
  SURFACE_DARK,
  SURFACE_LIGHT,
  snrStepExpression,
  type SnrPalette,
} from '@/styles/scale'
import { buildLanes, isAsymmetric, isFunctional, laneId, type ViewMode } from '@/map/lanes'
import {
  reliefColorExpression,
  TERRARIUM_ATTRIBUTION,
  TERRARIUM_MAXZOOM,
  TERRARIUM_URL,
  viewRange,
  type AltitudeRange,
} from '@/map/elevation'
import type { MapView } from '@/map/savedView'
import { issueFilter, NODE_ISSUES, type NodeIssue } from '@/map/nodeIssues'
import { clockFilter, clockImage } from '@/map/clock'
import { BRIDGE_LINK_DIM, bridgeFillExpression, bridgeRadiusExpression } from '@/map/bridge'

export const LINKS_SOURCE = 'links'
export const NODES_SOURCE = 'nodes'
/** Links heard in the last ACTIVE_MS, oriented in the direction of their newest packet. */
export const ACTIVE_SOURCE = 'links-active'
/** One lane per direction of each link, for the asymmetry view (see map/lanes.ts). */
export const LANES_SOURCE = 'lanes'
/** The selected link's per-direction readings, one line feature per direction. */
export const FOCUS_LABELS_SOURCE = 'focus-labels'
const PROFILE_MARK_SOURCE = 'profile-mark'
const SITING_SOURCE = 'siting'
const TRACE_PATH_SOURCE = 'trace-path'
const TRACE_LABELS_SOURCE = 'trace-labels'

const LAYER_CASING = 'links-casing'
const LAYER_TOPOLOGY = 'links-topology'
const LAYER_MEASURED = 'links-measured'
const LAYER_LANES = 'lanes-measured'
const LAYER_LANES_MISSING = 'lanes-missing'
const LAYER_LANE_ARROWS = 'lanes-arrows'
const LAYER_ACTIVE = 'links-active-line'
const LAYER_SELECTED = 'links-selected'
const LAYER_NODES = 'nodes-circles'
/** Listen-only observers: a diamond instead of a circle (api listenOnly). */
const LAYER_LISTEN = 'nodes-listen'
/** Companions: a violet phone, a personal device rather than infrastructure. */
const LAYER_COMPANIONS = 'nodes-companions'
const LAYER_NODE_SELECTED = 'nodes-selected'
const LAYER_NODE_WARN = 'nodes-hash-warn'
const LAYER_NODE_CLOCK = 'nodes-clock'
const LAYER_NODE_LABELS = 'nodes-labels'
const LAYER_FOCUS_LABELS = 'focus-labels'
const LAYER_PROFILE_MARK = 'profile-mark'
const LAYER_SITE_LINKS = 'siting-links'
const LAYER_SITE_RING = 'siting-ring'
const LAYER_SITE_NUMBERS = 'siting-numbers'
const SITE_RING = 'site-ring'
const LAYER_TRACE_LINE = 'trace-path-line'
const LAYER_TRACE_STOPS = 'trace-path-stops'
const LAYER_TRACE_NUMBERS = 'trace-path-numbers'
const LAYER_TRACE_LABELS = 'trace-labels'

/**
 * Tile source. OpenStreetMap's own tiles carry a usage policy that forbids
 * heavy automated use, so point this at your own raster cache before putting the
 * map in front of a group.
 * https://tile.openstreetmap.org
 */
const TILE_URL =
  import.meta.env.VITE_TILE_URL || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'

const EMPTY_FC = { type: 'FeatureCollection', features: [] } as const

const LAYER_ALTITUDE = 'altitude'
const ALTITUDE_SOURCE = 'terrain-dem'
/** Strong enough to read the ramp, light enough for the basemap to show through. */
const altitudeOpacity = (dark: boolean) => (dark ? 0.5 : 0.55)

/** How long a link animates after its newest packet. */
const ACTIVE_MS = 10_000

/**
 * Ant-path frames for `line-dasharray`, in line-width units: a 3-long dash every
 * 7. Growing the leading gap frame by frame moves the dashes toward the end of
 * the line, so the geometry's direction is the direction of travel.
 */
const DASH_FRAMES: number[][] = [
  [0, 4, 3],
  [0.5, 4, 2.5],
  [1, 4, 2],
  [1.5, 4, 1.5],
  [2, 4, 1],
  [2.5, 4, 0.5],
  [3, 4, 0],
  [0, 0.5, 3, 3.5],
  [0, 1, 3, 3],
  [0, 1.5, 3, 2.5],
  [0, 2, 3, 2],
  [0, 2.5, 3, 1.5],
  [0, 3, 3, 1],
  [0, 3.5, 3, 0.5],
]
const DASH_FRAME_MS = 50

const TOPOLOGY_FILTER: ExpressionSpecification = ['==', ['get', 'kind'], 'topology']
const MEASURED_FILTER: ExpressionSpecification = ['!=', ['get', 'kind'], 'topology']

interface ActiveProperties {
  linkId: string
  /** The lane of the newest packet's direction, hidden while this one animates. */
  laneId: string
  /** Whether the link is drawn as lanes in the asymmetry view (not topology). */
  lanes: boolean
  emphasized: boolean
  /** Usable in the "Fonctionnel" view (quality at or above the usable threshold). */
  functional: boolean
  weight: number
  /** SNR of the newest packet, else its direction's median, else the link quality. */
  snrActive?: number
  /** A leg of a trace sent from this browser: shown whatever the view filters. */
  trace?: boolean
}

const CHEVRON = 'chevron'
const DIAMOND = { light: 'diamond-light', dark: 'diamond-dark' } as const
const PHONE = { light: 'phone-light', dark: 'phone-dark' } as const
/** Companions' violet (theme --pt-routing), apart from the SNR ramp and node greys. */
const COMPANION_LIGHT = '#7c3aed'
const COMPANION_DARK = '#c4b5fd'
const IS_COMPANION: ExpressionSpecification = ['==', ['get', 'nodeType'], 'companion']
const IS_LISTEN_ONLY: ExpressionSpecification = ['==', ['get', 'listenOnly'], true]
const WARN = 'warn-1byte'
const CLOCK = 'clock-off'


/**
 * The ⚠ sign as an image: a yellow triangle with a dark "!". Drawn rather than
 * set as text because the ⚠ character renders as an outline, a colour emoji or
 * nothing at all depending on the fonts available. Matches the legend's icon.
 */
function warnImage(): ImageData {
  const px = 28 // 14 CSS px at pixelRatio 2
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = px
  const ctx = canvas.getContext('2d')!
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(14, 2.5)
  ctx.lineTo(26, 24.5)
  ctx.lineTo(2, 24.5)
  ctx.closePath()
  ctx.fillStyle = '#fab219'
  ctx.strokeStyle = '#1a1a19'
  ctx.lineWidth = 2.5
  ctx.fill()
  ctx.stroke()
  ctx.fillStyle = '#1a1a19'
  ctx.fillRect(12.6, 9, 2.8, 8.5)
  ctx.fillRect(12.6, 19.5, 2.8, 2.8)
  return ctx.getImageData(0, 0, px, px)
}

/** Node circles outside the "Rôle de pont" layer: repeaters a step bigger. */
const NODE_RADIUS = [
  'interpolate',
  ['linear'],
  ['zoom'],
  5,
  ['case', ['==', ['get', 'nodeType'], 'repeater'], 3, 2],
  12,
  ['case', ['==', ['get', 'nodeType'], 'repeater'], 6, 4],
] as unknown as ExpressionSpecification

/**
 * A listen-only observer: a diamond in the node circle's colours (surface fill,
 * no-data outline), so it reads as a node that relays nothing.
 */
function diamondImage(fill: string, stroke: string): ImageData {
  const px = 26 // 13 CSS px at pixelRatio 2
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = px
  const ctx = canvas.getContext('2d')!
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(13, 2.5)
  ctx.lineTo(23.5, 13)
  ctx.lineTo(13, 23.5)
  ctx.lineTo(2.5, 13)
  ctx.closePath()
  ctx.fillStyle = fill
  ctx.strokeStyle = stroke
  ctx.lineWidth = 4 // 2 CSS px, like the circles' outline
  ctx.fill()
  ctx.stroke()
  return ctx.getImageData(0, 0, px, px)
}

/**
 * A companion: a phone outline (tall rounded body, its screen filled) in
 * violet, a hue nothing else on the map uses, so personal devices read apart
 * from the mesh's infrastructure.
 */
function phoneImage(color: string, fill: string): ImageData {
  const w = 18 // 9 x 13 CSS px at pixelRatio 2
  const h = 26
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!
  ctx.beginPath()
  ctx.roundRect(2, 2, w - 4, h - 4, 3.5)
  ctx.fillStyle = fill
  ctx.strokeStyle = color
  ctx.lineWidth = 3
  ctx.fill()
  ctx.stroke()
  ctx.fillStyle = color
  ctx.fillRect(5.5, 6, w - 11, h - 14) // the screen
  return ctx.getImageData(0, 0, w, h)
}

/**
 * Width is driven by traffic on a log scale (`weight` = log10(samples+1)): a
 * backbone link carries orders of magnitude more than a leaf, and a linear width
 * would collapse everything but the busiest pair into a hairline.
 *
 * MapLibre requires a `zoom` expression to be the input of a TOP-LEVEL step or
 * interpolate, so the extra width of the casing and the selection ring is baked
 * into the inner outputs rather than wrapped in an arithmetic expression.
 */
function widthAt(zoom: 5 | 11, extra: number): ExpressionSpecification {
  const [lo, hi] = zoom === 5 ? [0.8, 2.5] : [1.6, 6]
  return ['interpolate', ['linear'], ['get', 'weight'], 0, lo + extra, 4, hi + extra]
}

function widthExpr(extra = 0): ExpressionSpecification {
  return ['interpolate', ['linear'], ['zoom'], 5, widthAt(5, extra), 11, widthAt(11, extra)]
}

const WIDTH = widthExpr()

/*
 * Asymmetry view geometry. Lanes have a fixed width per zoom (traffic no longer
 * drives it) so the direction chevrons can sit exactly on them. Each lane is
 * offset by half its width plus a 0.5px gap, to the right of its direction.
 */
type LaneStops = { 5: number; 11: number; 15: number }
const LANE_WIDTH_AT: LaneStops = { 5: 1.5, 11: 3.5, 15: 6 }
const LANE_OFFSET_AT: LaneStops = { 5: 1.25, 11: 2.25, 15: 3.5 }
/** Both lanes plus a 1.5px surface ring each side. */
const LANE_CASING_AT: LaneStops = { 5: 7, 11: 11, 15: 16 }
const LANE_SELECTED_AT: LaneStops = LANE_CASING_AT

/**
 * Zoom-interpolated: `lanes` stops where `hasLanes` holds, else `otherwise` or
 * the traffic width. The zoom has to stay the top-level input, so the per-feature
 * case goes inside each stop.
 */
function laneAware(
  hasLanes: ExpressionSpecification,
  lanes: LaneStops,
  extra: number,
  otherwise?: number,
): ExpressionSpecification {
  // Traffic widths stop growing at zoom 11; lanes keep growing to 15.
  const other = (z: 5 | 11) => otherwise ?? widthAt(z, extra)
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    5,
    ['case', hasLanes, lanes[5], other(5)],
    11,
    ['case', hasLanes, lanes[11], other(11)],
    15,
    ['case', hasLanes, lanes[15], other(11)],
  ]
}

function laneStops(stops: LaneStops): ExpressionSpecification {
  return ['interpolate', ['linear'], ['zoom'], 5, stops[5], 11, stops[11], 15, stops[15]]
}

const LINK_HAS_LANES: ExpressionSpecification = ['!=', ['get', 'kind'], 'topology']
const ACTIVE_HAS_LANES: ExpressionSpecification = ['==', ['get', 'lanes'], true]
const LANE_WIDTH = laneStops(LANE_WIDTH_AT)
const LANE_OFFSET = laneStops(LANE_OFFSET_AT)
/**
 * Chevrons are drawn 10px tall and scaled with the lane. `icon-offset` is in
 * icon units, so the lane offset is divided by the icon size at each stop.
 */
const CHEVRON_SIZE_AT = { 10: 0.45, 15: 0.8 } as const
const CHEVRON_SIZE: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  10,
  CHEVRON_SIZE_AT[10],
  15,
  CHEVRON_SIZE_AT[15],
]
// Lane offset at zoom 10, interpolated between the 5 and 11 stops.
const LANE_OFFSET_Z10 = LANE_OFFSET_AT[5] + ((LANE_OFFSET_AT[11] - LANE_OFFSET_AT[5]) * 5) / 6
const CHEVRON_OFFSET: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  10,
  ['literal', [0, LANE_OFFSET_Z10 / CHEVRON_SIZE_AT[10]]],
  15,
  ['literal', [0, LANE_OFFSET_AT[15] / CHEVRON_SIZE_AT[15]]],
]
const EMPHASIZED: ExpressionSpecification = ['==', ['get', 'emphasized'], true]

/**
 * Opacity of every link layer, set in one place (applyFocus) because selecting
 * a link fades all the others: the selected link is drawn at full opacity and
 * the rest drops to FOCUS_DIM, so it reads on its own against the map.
 */
const FOCUS_DIM = 0.07
const NODE_FOCUS_DIM = 0.25

/**
 * On-screen heading of travel from a to b in degrees, clockwise (screen y
 * points down). The map never rotates or tilts, so Web Mercator alone gives
 * it at any zoom.
 */
function travelAngle(a: [number, number], b: [number, number]): number {
  const y = (lat: number) => -Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))
  const dx = ((b[0] - a[0]) * Math.PI) / 180
  const dy = y(b[1]) - y(a[1])
  return (Math.atan2(dy, dx) * 180) / Math.PI
}

/**
 * Where the label of the direction from → to sits: on the right of travel,
 * the side its lane is drawn on (positive line-offset), so in the "Par sens"
 * view each reading lies against its own lane, whichever way the link runs.
 */
function directionSide(from: [number, number], to: [number, number]) {
  return focusSide(travelAngle(from, to), -1)
}

interface FocusLabel {
  text: string
  color: string
  anchor: string
  offset: [number, number]
}

/**
 * Where an upright label sits beside a link drawn at `angle` degrees: pushed
 * along the link's normal to one side (side -1 or 1), and anchored by the edge
 * facing the link, so it clears the line whatever its angle and the text's
 * length. Offsets are in ems, screen axes (y down).
 */
function focusSide(angle: number, side: -1 | 1): Pick<FocusLabel, 'anchor' | 'offset'> {
  const rad = (angle * Math.PI) / 180
  // side -1: the right of travel at this heading; side 1: the left.
  const nx = Math.sin(rad) * side
  const ny = -Math.cos(rad) * side
  const v = ny < -0.38 ? 'bottom' : ny > 0.38 ? 'top' : ''
  const h = nx > 0.38 ? 'left' : nx < -0.38 ? 'right' : ''
  const anchor = v && h ? `${v}-${h}` : v || h || 'center'
  return { anchor, offset: [nx * 0.7, ny * 0.7] }
}

/** A right-pointing chevron in white, used as an SDF icon tinted per theme. */
function chevronImage(): ImageData {
  const px = 20 // 10 CSS px at pixelRatio 2
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = px
  const ctx = canvas.getContext('2d')!
  ctx.strokeStyle = '#fff'
  ctx.lineWidth = 4
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(6, 3)
  ctx.lineTo(14, 10)
  ctx.lineTo(6, 17)
  ctx.stroke()
  return ctx.getImageData(0, 0, px, px)
}

/** Clicking a 2px line demands pixel precision nobody has; query a padded box. */
const CLICK_TOLERANCE = 6

function baseStyle(dark: boolean): StyleSpecification {
  return {
    version: 8,
    glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
    sources: {
      osm: {
        type: 'raster',
        tiles: [TILE_URL],
        tileSize: 256,
        maxzoom: 19,
        attribution: '&copy; OpenStreetMap contributors',
      },
      [LINKS_SOURCE]: { type: 'geojson', data: EMPTY_FC as never },
      [NODES_SOURCE]: { type: 'geojson', data: EMPTY_FC as never },
      [ACTIVE_SOURCE]: { type: 'geojson', data: EMPTY_FC as never },
      [LANES_SOURCE]: { type: 'geojson', data: EMPTY_FC as never },
      [FOCUS_LABELS_SOURCE]: { type: 'geojson', data: EMPTY_FC as never },
      [PROFILE_MARK_SOURCE]: { type: 'geojson', data: EMPTY_FC as never },
      [SITING_SOURCE]: { type: 'geojson', data: EMPTY_FC as never },
      [TRACE_PATH_SOURCE]: { type: 'geojson', data: EMPTY_FC as never },
      [TRACE_LABELS_SOURCE]: { type: 'geojson', data: EMPTY_FC as never },
      [ALTITUDE_SOURCE]: {
        type: 'raster-dem',
        tiles: [TERRARIUM_URL],
        encoding: 'terrarium',
        tileSize: 256,
        maxzoom: TERRARIUM_MAXZOOM,
        attribution: TERRARIUM_ATTRIBUTION,
      },
    },
    layers: [
      {
        id: 'basemap',
        type: 'raster',
        source: 'osm',
        paint: {
          // The basemap is context, not data. Desaturating it keeps the one
          // encoded hue on the map the link colour.
          'raster-saturation': -0.75,
          'raster-opacity': dark ? 0.45 : 0.75,
          'raster-brightness-min': dark ? 0.0 : 0.15,
          'raster-brightness-max': dark ? 0.55 : 1,
        },
      },
      // Altitude as colour, off until switched on (applyAltitude). The ramp is
      // re-stretched to the view as it moves.
      {
        id: LAYER_ALTITUDE,
        type: 'color-relief',
        source: ALTITUDE_SOURCE,
        layout: { visibility: 'none' },
        paint: {
          'color-relief-opacity': altitudeOpacity(dark),
          'color-relief-color': reliefColorExpression({ lo: 0, hi: 300 }) as never,
        },
      },
    ],
  }
}

/** Measured and trace links: the weaker direction's SNR on the ramp. */
function measuredColor(dark: boolean, thresholds: readonly number[], palette: SnrPalette) {
  return snrStepExpression(thresholds, dark, 'snrQuality', palette) as unknown as ExpressionSpecification
}

/** Animated links: the newest packet's SNR when it had one, ink otherwise. */
function activeColor(dark: boolean, thresholds: readonly number[], palette: SnrPalette) {
  return [
    'case',
    ['has', 'snrActive'],
    snrStepExpression(thresholds, dark, 'snrActive', palette),
    dark ? NO_DATA_DARK : NO_DATA_LIGHT,
  ] as unknown as ExpressionSpecification
}

/**
 * The "Fonctionnel" view: both directions measured, the weaker at the usable
 * threshold or above. Same rule as isFunctional in map/lanes.ts.
 */
function functionalFilter(thresholds: readonly number[]): ExpressionSpecification {
  return [
    'all',
    ['in', ['get', 'snrBasis'], ['literal', ['both', 'few']]],
    ['>=', ['coalesce', ['get', 'snrQuality'], -1000], thresholds[1] ?? -5],
  ]
}

function dataLayers(dark: boolean, thresholds: readonly number[], palette: SnrPalette) {
  const surface = dark ? SURFACE_DARK : SURFACE_LIGHT
  const noData = dark ? NO_DATA_DARK : NO_DATA_LIGHT

  return [
    // A surface-coloured casing under every line. Map tiles are a busy, uneven
    // background, so without it the lighter ramp steps disappear over pale
    // terrain and the darker ones over forest.
    {
      id: LAYER_CASING,
      type: 'line' as const,
      source: LINKS_SOURCE,
      layout: { 'line-cap': 'round' as const, 'line-join': 'round' as const },
      paint: {
        'line-color': surface,
        'line-width': widthExpr(3),
      },
    },
    // The selection is a halo under the lines, so the selected link keeps its
    // own colours (and both lanes in the asymmetry view).
    {
      id: LAYER_SELECTED,
      type: 'line' as const,
      source: LINKS_SOURCE,
      filter: ['==', ['get', 'linkId'], '__none__'] as ExpressionSpecification,
      layout: { 'line-cap': 'round' as const, 'line-join': 'round' as const },
      paint: {
        'line-color': dark ? '#ffffff' : '#0b0b0b',
        'line-width': widthExpr(2.5),
        'line-opacity': 0.9,
      },
    },
    // Topology links: the pair demonstrably hear each other, but no signal
    // measurement exists for them. Dashed and in ink, never a ramp step, so the
    // map cannot imply a quality it does not have.
    {
      id: LAYER_TOPOLOGY,
      type: 'line' as const,
      source: LINKS_SOURCE,
      filter: TOPOLOGY_FILTER,
      layout: { 'line-cap': 'butt' as const, 'line-join': 'round' as const },
      paint: {
        'line-color': noData,
        'line-width': WIDTH,
        'line-dasharray': [2, 2] as unknown as ExpressionSpecification,
      },
    },
    // Measured and trace links: a real SNR, on the validated ordinal ramp,
    // taken from the weaker direction (snrQuality).
    {
      id: LAYER_MEASURED,
      type: 'line' as const,
      source: LINKS_SOURCE,
      filter: MEASURED_FILTER,
      layout: { 'line-cap': 'round' as const, 'line-join': 'round' as const },
      paint: {
        'line-color': measuredColor(dark, thresholds, palette),
        'line-width': WIDTH,
      },
    },
    // Asymmetry view: one lane per direction, coloured by that direction's
    // median. Hidden in the quality view (see applyView). A direction never
    // measured is a thin dashed ink lane rather than an invented colour.
    {
      id: LAYER_LANES_MISSING,
      type: 'line' as const,
      source: LANES_SOURCE,
      filter: ['==', ['get', 'measured'], false] as ExpressionSpecification,
      layout: { 'line-cap': 'butt' as const, 'line-join': 'round' as const, visibility: 'none' as const },
      paint: {
        'line-color': noData,
        'line-width': 1,
        'line-offset': LANE_OFFSET,
        'line-dasharray': [2, 2] as unknown as ExpressionSpecification,
      },
    },
    {
      id: LAYER_LANES,
      type: 'line' as const,
      source: LANES_SOURCE,
      filter: ['==', ['get', 'measured'], true] as ExpressionSpecification,
      layout: { 'line-cap': 'butt' as const, 'line-join': 'round' as const, visibility: 'none' as const },
      paint: {
        'line-color': snrStepExpression(thresholds, dark, 'snr', palette) as unknown as ExpressionSpecification,
        'line-width': LANE_WIDTH,
        'line-offset': LANE_OFFSET,
      },
    },
    // Chevrons in surface colour, cut into each lane, pointing its way.
    {
      id: LAYER_LANE_ARROWS,
      type: 'symbol' as const,
      source: LANES_SOURCE,
      minzoom: 10,
      layout: {
        visibility: 'none' as const,
        'symbol-placement': 'line' as const,
        'symbol-spacing': 90,
        'icon-image': CHEVRON,
        'icon-size': CHEVRON_SIZE,
        'icon-offset': CHEVRON_OFFSET,
        'icon-rotation-alignment': 'map' as const,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: {
        'icon-color': ['case', ['==', ['get', 'measured'], true], surface, noData] as ExpressionSpecification,
      },
    },
    // Recently heard links, animated in the direction of their newest packet.
    // The static line underneath is hidden meanwhile (see syncActive), so the
    // dash gaps show the casing rather than the same colour.
    {
      id: LAYER_ACTIVE,
      type: 'line' as const,
      source: ACTIVE_SOURCE,
      layout: { 'line-cap': 'butt' as const, 'line-join': 'round' as const },
      paint: {
        'line-color': activeColor(dark, thresholds, palette),
        'line-width': widthExpr(1),
        'line-dasharray': DASH_FRAMES[0] as unknown as ExpressionSpecification,
      },
    },
    // The path being built for a trace (stores/trace.ts), from the companion
    // through the picked nodes in order.
    {
      id: LAYER_TRACE_LINE,
      type: 'line' as const,
      source: TRACE_PATH_SOURCE,
      filter: ['==', ['geometry-type'], 'LineString'] as ExpressionSpecification,
      layout: { 'line-cap': 'round' as const, 'line-join': 'round' as const },
      paint: {
        'line-color': dark ? '#3987e5' : '#2a78d6',
        'line-width': 3,
        'line-dasharray': [1, 1.6],
        'line-opacity': 0.9,
      },
    },
    {
      id: LAYER_NODES,
      type: 'circle' as const,
      source: NODES_SOURCE,
      filter: ['all', ['!', IS_LISTEN_ONLY], ['!', IS_COMPANION]] as ExpressionSpecification,
      paint: {
        'circle-radius': NODE_RADIUS,
        'circle-color': dark ? SURFACE_DARK : SURFACE_LIGHT,
        'circle-stroke-width': 2,
        'circle-stroke-color': dark ? NO_DATA_DARK : NO_DATA_LIGHT,
      },
    },
    {
      id: LAYER_LISTEN,
      type: 'symbol' as const,
      source: NODES_SOURCE,
      filter: IS_LISTEN_ONLY,
      layout: {
        'icon-image': dark ? DIAMOND.dark : DIAMOND.light,
        'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 0.6, 12, 1] as ExpressionSpecification,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    },
    {
      id: LAYER_COMPANIONS,
      type: 'symbol' as const,
      source: NODES_SOURCE,
      filter: ['all', IS_COMPANION, ['!', IS_LISTEN_ONLY]] as ExpressionSpecification,
      layout: {
        'icon-image': dark ? PHONE.dark : PHONE.light,
        'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 0.6, 12, 1] as ExpressionSpecification,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    },
    // A ring around the selected node, in text ink like the link selection.
    {
      id: LAYER_NODE_SELECTED,
      type: 'circle' as const,
      source: NODES_SOURCE,
      filter: ['==', ['get', 'key'], '__none__'] as ExpressionSpecification,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 7, 12, 11] as ExpressionSpecification,
        'circle-color': 'rgba(0, 0, 0, 0)',
        'circle-stroke-width': 2.5,
        'circle-stroke-color': dark ? '#ffffff' : '#0b0b0b',
      },
    },
    // ⚠ beside nodes still on 1-byte path hashes. Offset to the upper right so
    // the node circle keeps its size and stays clickable; above the selection
    // ring so a selected node keeps its flag.
    {
      id: LAYER_NODE_WARN,
      type: 'symbol' as const,
      source: NODES_SOURCE,
      filter: issueFilter(NODE_ISSUES), // narrowed by applyView to the enabled issues
      layout: {
        'icon-image': WARN,
        'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 0.75, 12, 1] as ExpressionSpecification,
        'icon-offset': [11, -11] as [number, number],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    },
    // A clock beside nodes whose clock is off, upper left: the ⚠ has the
    // upper right. Filter set by applyView (threshold, legend switch).
    {
      id: LAYER_NODE_CLOCK,
      type: 'symbol' as const,
      source: NODES_SOURCE,
      filter: ['boolean', false] as ExpressionSpecification,
      layout: {
        'icon-image': CLOCK,
        'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 0.75, 12, 1] as ExpressionSpecification,
        'icon-offset': [-11, -11] as [number, number],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    },
    {
      id: LAYER_NODE_LABELS,
      type: 'symbol' as const,
      source: NODES_SOURCE,
      minzoom: 8,
      layout: {
        'text-field': ['get', 'name'] as unknown as ExpressionSpecification,
        'text-size': 11,
        'text-offset': [0, 1.1] as [number, number],
        'text-anchor': 'top' as const,
        'text-allow-overlap': false,
      },
      paint: {
        // Labels wear text ink, never a series colour.
        'text-color': dark ? '#ffffff' : '#0b0b0b',
        'text-halo-color': dark ? SURFACE_DARK : SURFACE_LIGHT,
        'text-halo-width': 1.4,
      },
    },
    // The selected link's SNR and RSSI, one upright label per direction in the
    // sender's colour, at the link's midpoint beside its own lane (directionSide).
    // Asymmetry view only (see applyView): the others show one value per link.
    {
      id: LAYER_FOCUS_LABELS,
      type: 'symbol' as const,
      source: FOCUS_LABELS_SOURCE,
      layout: {
        'text-field': ['get', 'text'] as ExpressionSpecification,
        'text-size': 12.5,
        // One line each: wrapped, the two labels would run into each other.
        'text-max-width': 60,
        visibility: 'none' as const,
        'text-anchor': ['get', 'anchor'] as ExpressionSpecification,
        'text-offset': ['get', 'offset'] as ExpressionSpecification,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': ['get', 'color'] as ExpressionSpecification,
        'text-halo-color': surface,
        'text-halo-width': 2,
      },
    },
    // Picked nodes of the trace path, numbered in order over their circle.
    {
      id: LAYER_TRACE_STOPS,
      type: 'circle' as const,
      source: TRACE_PATH_SOURCE,
      filter: ['==', ['geometry-type'], 'Point'] as ExpressionSpecification,
      paint: {
        // Wide enough for "1|3": grows with the text.
        'circle-radius': ['+', 6, ['*', 3, ['length', ['get', 'n']]]] as ExpressionSpecification,
        'circle-color': dark ? '#3987e5' : '#2a78d6',
        'circle-stroke-color': surface,
        'circle-stroke-width': 2,
      },
    },
    {
      id: LAYER_TRACE_NUMBERS,
      type: 'symbol' as const,
      source: TRACE_PATH_SOURCE,
      filter: ['==', ['geometry-type'], 'Point'] as ExpressionSpecification,
      layout: {
        'text-field': ['get', 'n'] as ExpressionSpecification,
        'text-size': 11,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': '#ffffff' },
    },
    // The SNR a returned trace measured on each of its links, laid out like the
    // selected link's labels but shown in every view: the user asked for it.
    {
      id: LAYER_TRACE_LABELS,
      type: 'symbol' as const,
      source: TRACE_LABELS_SOURCE,
      layout: {
        'text-field': ['get', 'text'] as ExpressionSpecification,
        'text-size': 12.5,
        'text-max-width': 60,
        'text-anchor': ['get', 'anchor'] as ExpressionSpecification,
        'text-offset': ['get', 'offset'] as ExpressionSpecification,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': ['get', 'color'] as ExpressionSpecification,
        'text-halo-color': surface,
        'text-halo-width': 2,
      },
    },
    // The point under the pointer on the line-of-sight chart, placed on the link.
    {
      id: LAYER_PROFILE_MARK,
      type: 'circle' as const,
      source: PROFILE_MARK_SOURCE,
      paint: {
        'circle-radius': 6,
        'circle-color': dark ? '#3987e5' : '#2a78d6',
        'circle-stroke-color': surface,
        'circle-stroke-width': 2.5,
      },
    },
    // Proposed sites ("Où placer un nœud ?"): the links each would make,
    // dashed, then the turning yellow and black ring, numbered as in the panel.
    {
      id: LAYER_SITE_LINKS,
      type: 'line' as const,
      source: SITING_SOURCE,
      filter: ['==', ['geometry-type'], 'LineString'] as ExpressionSpecification,
      paint: {
        'line-color': dark ? '#fab219' : '#b7791f',
        'line-width': 1.8,
        'line-dasharray': [3, 2],
        'line-opacity': ['case', ['get', 'dim'], 0.2, 0.95] as ExpressionSpecification,
      },
    },
    {
      id: LAYER_SITE_RING,
      type: 'symbol' as const,
      source: SITING_SOURCE,
      filter: ['==', ['geometry-type'], 'Point'] as ExpressionSpecification,
      layout: {
        'icon-image': SITE_RING,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: { 'icon-opacity': ['case', ['get', 'dim'], 0.45, 1] as ExpressionSpecification },
    },
    {
      id: LAYER_SITE_NUMBERS,
      type: 'symbol' as const,
      source: SITING_SOURCE,
      filter: ['==', ['geometry-type'], 'Point'] as ExpressionSpecification,
      layout: {
        'text-field': ['get', 'n'] as ExpressionSpecification,
        'text-size': 12,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': dark ? '#ffffff' : '#0b0b0b',
        'text-halo-color': surface,
        'text-halo-width': 2,
      },
    },
  ]
}

/**
 * The proposed-site ring as an animated image: yellow and black dashes turning
 * around the point, redrawn every frame while a site is on screen.
 */
function siteRingImage(m: maplibregl.Map): maplibregl.StyleImageInterface {
  const size = 60 // 30 CSS px at pixelRatio 2
  const r = size / 2 - 5
  const dash = (2 * Math.PI * r) / 12
  let ctx: CanvasRenderingContext2D | null = null
  const image: maplibregl.StyleImageInterface = {
    width: size,
    height: size,
    data: new Uint8Array(size * size * 4),
    onAdd() {
      const canvas = document.createElement('canvas')
      canvas.width = canvas.height = size
      ctx = canvas.getContext('2d', { willReadFrequently: true })
    },
    render() {
      if (!ctx) return false
      ctx.clearRect(0, 0, size, size)
      ctx.lineWidth = 6
      ctx.setLineDash([])
      ctx.strokeStyle = '#1a1a19'
      ctx.beginPath()
      ctx.arc(size / 2, size / 2, r, 0, Math.PI * 2)
      ctx.stroke()
      ctx.strokeStyle = '#fab219'
      ctx.setLineDash([dash, dash])
      // One turn every 6 s.
      ctx.lineDashOffset = -((performance.now() / 6000) * 2 * Math.PI * r)
      ctx.beginPath()
      ctx.arc(size / 2, size / 2, r, 0, Math.PI * 2)
      ctx.stroke()
      image.data = ctx.getImageData(0, 0, size, size).data
      m.triggerRepaint()
      return true
    },
  }
  return image
}

/**
 * A measurement stays on the map for the retention period (14 days by default)
 * after the link was last heard, fading as it ages: full strength for the
 * first day, then down to MEASURE_FADE_MIN at the end of retention. Links with
 * no measurement are not affected.
 */
const MEASURE_FRESH_SEC = 86_400
const MEASURE_FADE_MIN = 0.35

function measureFade(base: number, retentionSec: number): ExpressionSpecification {
  const end = Math.max(retentionSec, MEASURE_FRESH_SEC + 1)
  return [
    '*',
    base,
    [
      'interpolate',
      ['linear'],
      ['coalesce', ['get', 'measureAgeSec'], 0],
      MEASURE_FRESH_SEC,
      1,
      end,
      MEASURE_FADE_MIN,
    ],
  ]
}

/** Unfocused opacity per link layer. Every lane at full strength: the "Liens
 * déséquilibrés seulement" filter is what singles out unbalanced links. */
const LINK_OPACITY: { layer: string; prop: 'line-opacity' | 'icon-opacity'; base: number }[] = [
  { layer: LAYER_CASING, prop: 'line-opacity', base: 0.85 },
  { layer: LAYER_TOPOLOGY, prop: 'line-opacity', base: 0.45 },
  { layer: LAYER_MEASURED, prop: 'line-opacity', base: 0.95 },
  { layer: LAYER_LANES_MISSING, prop: 'line-opacity', base: 0.9 },
  { layer: LAYER_LANES, prop: 'line-opacity', base: 1 },
  { layer: LAYER_LANE_ARROWS, prop: 'icon-opacity', base: 1 },
  { layer: LAYER_ACTIVE, prop: 'line-opacity', base: 1 },
]

export interface Padding {
  top: number
  right: number
  bottom: number
  left: number
}

export interface UseMapOptions {
  container: Ref<HTMLElement | null>
  dark: Ref<boolean>
  thresholds: Ref<number[]>
  mode: Ref<ViewMode>
  /** Asymmetry view only: hide every link that is not asymmetric. */
  asymOnly: Ref<boolean>
  asymThreshold: Ref<number>
  palette: Ref<SnrPalette>
  /** Node configuration issues the ⚠ flags. */
  issues: Ref<NodeIssue[]>
  /** Clock offset (s) from which a node gets the clock; null hides the clocks. */
  clockMaxSec: Ref<number | null>
  /** How long a link keeps its signal values; links fade over it. */
  measureRetentionSec: Ref<number>
  /** Nodes sized and filled by their bridge role (map/bridge), links dimmed. */
  bridge: Ref<boolean>
  /** Altitude as a colour ramp under the links. */
  altitude: Ref<boolean>
  /** The altitudes the ramp currently spans, or null while it is off. */
  onAltitudeRange?: (range: AltitudeRange | null) => void
  center?: [number, number]
  zoom?: number
  onMoveEnd: (bbox: BBox) => void
  /** Centre and zoom after every move, to restore the view on the next visit. */
  onViewChange?: (view: MapView) => void
  onSelectLink: (linkId: string | null) => void
  onSelectNode: (key: string | null) => void
  /** A proposed site clicked on the map. */
  onSelectSite?: (id: string) => void
}

export function useMapLibre(opts: UseMapOptions) {
  let map: MlMap | null = null
  let ready = false
  let pendingLinks: FeatureCollection<LinkProperties> | null = null
  let pendingNodes: FeatureCollection<NodeProperties> | null = null
  let pendingSites: { type: 'FeatureCollection'; features: unknown[] } | null = null

  /** Animated links; `local` ones come from a trace sent here, not from the server. */
  const active = new Map<
    string,
    { expiresAt: number; feature: Feature<ActiveProperties, LineString>; local?: boolean }
  >()
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  let raf = 0
  let dashFrame = 0
  let lastDashAt = 0
  /** Links asymmetric beyond the threshold, for the "asymmetric only" filter. */
  let emphasizedIds: string[] = []
  /** The selected link and its two ends, which stay opaque while the rest fades. */
  let selectedLink: string | null = null
  let selectedNode: string | null = null
  let focusLinks: string[] = []
  let focusNodes: string[] = []
  /** A trace in flight or just back: its nodes and links stay, the rest fades. */
  let traceFocus: { links: string[]; nodes: string[] } | null = null

  function bboxOf(m: MlMap): BBox {
    const b = m.getBounds()
    return {
      minLng: b.getWest(),
      minLat: b.getSouth(),
      maxLng: b.getEast(),
      maxLat: b.getNorth(),
    }
  }

  function mount() {
    const el = opts.container.value
    if (!el || map) return

    map = new maplibregl.Map({
      container: el,
      style: baseStyle(opts.dark.value),
      center: opts.center ?? [-1.6778, 48.1173],
      zoom: opts.zoom ?? 8,
      attributionControl: { compact: true },
      // Rotation adds nothing to reading a link graph and makes the labels worse.
      pitchWithRotate: false,
      dragRotate: false,
    })
    // Exposed for debugging from the console and for end-to-end screenshots.
    ;(window as unknown as { __meshqualMap?: MlMap }).__meshqualMap = map
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left')

    map.on('load', () => {
      if (!map) return
      map.addImage(CHEVRON, chevronImage(), { pixelRatio: 2, sdf: true })
      map.addImage(WARN, warnImage(), { pixelRatio: 2 })
      map.addImage(CLOCK, clockImage(), { pixelRatio: 2 })
      map.addImage(DIAMOND.light, diamondImage(SURFACE_LIGHT, NO_DATA_LIGHT), { pixelRatio: 2 })
      map.addImage(DIAMOND.dark, diamondImage(SURFACE_DARK, NO_DATA_DARK), { pixelRatio: 2 })
      map.addImage(PHONE.light, phoneImage(COMPANION_LIGHT, SURFACE_LIGHT), { pixelRatio: 2 })
      map.addImage(PHONE.dark, phoneImage(COMPANION_DARK, SURFACE_DARK), { pixelRatio: 2 })
      map.addImage(SITE_RING, siteRingImage(map), { pixelRatio: 2 })
      addDataLayers()
      ready = true
      applyView()
      applyFocus()
      applyAltitude()
      applyBridge()
      if (pendingLinks) setLinks(pendingLinks)
      if (pendingNodes) setNodes(pendingNodes)
      if (pendingSites) setSites(pendingSites)
      opts.onMoveEnd(bboxOf(map))
    })

    map.on('moveend', () => {
      if (!map) return
      opts.onMoveEnd(bboxOf(map))
      const c = map.getCenter()
      opts.onViewChange?.({ center: [c.lng, c.lat], zoom: map.getZoom() })
      if (opts.altitude.value) scheduleAltitudeStretch()
    })

    const hitAt = (point: maplibregl.Point) => {
      if (!map) return []
      const box: [maplibregl.PointLike, maplibregl.PointLike] = [
        [point.x - CLICK_TOLERANCE, point.y - CLICK_TOLERANCE],
        [point.x + CLICK_TOLERANCE, point.y + CLICK_TOLERANCE],
      ]
      return map.queryRenderedFeatures(box, {
        layers: [LAYER_ACTIVE, LAYER_LANES, LAYER_LANES_MISSING, LAYER_MEASURED, LAYER_TOPOLOGY],
      })
    }

    // Nodes sit on top of links and win a click that touches both. Among
    // close nodes the nearest to the pointer wins, not whichever drew last.
    const nodeAt = (point: maplibregl.Point): string | null => {
      const m = map
      if (!m) return null
      const box: [maplibregl.PointLike, maplibregl.PointLike] = [
        [point.x - CLICK_TOLERANCE, point.y - CLICK_TOLERANCE],
        [point.x + CLICK_TOLERANCE, point.y + CLICK_TOLERANCE],
      ]
      let best: string | null = null
      let bestD = Infinity
      for (const f of m.queryRenderedFeatures(box, { layers: [LAYER_NODES, LAYER_LISTEN, LAYER_COMPANIONS] })) {
        const key = f.properties?.['key']
        if (typeof key !== 'string' || f.geometry.type !== 'Point') continue
        const p = m.project(f.geometry.coordinates as [number, number])
        const d = (p.x - point.x) ** 2 + (p.y - point.y) ** 2
        if (d < bestD) {
          best = key
          bestD = d
        }
      }
      return best
    }

    const siteAt = (point: maplibregl.Point): string | null => {
      if (!map) return null
      const box: [maplibregl.PointLike, maplibregl.PointLike] = [
        [point.x - CLICK_TOLERANCE, point.y - CLICK_TOLERANCE],
        [point.x + CLICK_TOLERANCE, point.y + CLICK_TOLERANCE],
      ]
      const id = map.queryRenderedFeatures(box, { layers: [LAYER_SITE_RING] })[0]?.properties?.['site']
      return typeof id === 'string' ? id : null
    }

    map.on('click', (e) => {
      const site = siteAt(e.point)
      if (site) {
        opts.onSelectSite?.(site)
        return
      }
      const node = nodeAt(e.point)
      if (node) {
        opts.onSelectNode(node)
        return
      }
      const id = hitAt(e.point)[0]?.properties?.['linkId']
      if (typeof id === 'string') {
        opts.onSelectLink(id)
        return
      }
      opts.onSelectLink(null)
      opts.onSelectNode(null)
    })

    map.on('mousemove', (e) => {
      if (!map) return
      map.getCanvas().style.cursor = siteAt(e.point) || nodeAt(e.point) || hitAt(e.point).length > 0 ? 'pointer' : ''
    })
  }

  function addDataLayers() {
    if (!map) return
    for (const layer of dataLayers(opts.dark.value, opts.thresholds.value, opts.palette.value)) {
      if (!map.getLayer(layer.id)) map.addLayer(layer as never)
    }
  }

  /** The links last handed to the map, by id, to send only what changed. */
  let shownLinks = new Map<string, Feature<LinkProperties>>()

  function setLinks(fc: FeatureCollection<LinkProperties>) {
    pendingLinks = fc
    if (!map || !ready) return
    const next = new Map(fc.features.map((f) => [f.properties.linkId, f]))
    // The store keeps an unchanged link's object as it was (stores/mesh
    // patchLinks): a new object is a new or updated link.
    const changed = fc.features.filter((f) => shownLinks.get(f.properties.linkId) !== f)
    const removed = [...shownLinks.keys()].filter((id) => !next.has(id))
    const src = map.getSource(LINKS_SOURCE) as maplibregl.GeoJSONSource | undefined
    const incremental = shownLinks.size > 0 && changed.length + removed.length < fc.features.length / 4
    if (src) {
      if (incremental) {
        // A live update touches a few links: re-tiling all of them on every
        // event is what costs most on a phone.
        if (changed.length || removed.length) src.updateData({ add: changed as never, remove: removed })
      } else {
        // One setData call replaces the whole layer. This is why the API returns
        // GeoJSON: there is no transform step between fetch and render.
        src.setData(fc as never)
      }
    }
    shownLinks = next
    const threshold = opts.asymThreshold.value
    // Lanes are drawn only in the "Par sens" view: built when it shows.
    if (opts.mode.value === 'asymmetry') syncLanes()
    emphasizedIds = fc.features
      .filter((f) => isAsymmetric(f.properties, threshold))
      .map((f) => f.properties.linkId)
    // Fresh readings for the selected link's labels.
    if (selectedLink) updateFocusLabels()
    // A refresh can bring in more of a selected node's links (after a zoom out).
    if (selectedNode) {
      computeFocus()
      applyFocus()
    }
    // An unchanged link keeps the age it was fetched with: only new or updated
    // links can start an animation.
    trackActive(incremental ? changed : fc.features, next)
  }

  function syncLanes() {
    const lanes = map?.getSource(LANES_SOURCE)
    if (pendingLinks && lanes && 'setData' in lanes) {
      ;(lanes as maplibregl.GeoJSONSource).setData(buildLanes(pendingLinks, opts.asymThreshold.value) as never)
    }
  }

  /**
   * Records which links were heard in the last ACTIVE_MS. The expiry comes from
   * the server-computed `ageSec` and the local clock at fetch time, so a skewed
   * client clock cannot keep a link animated or cut it short.
   */
  function trackActive(features: Feature<LinkProperties>[], present: Map<string, unknown>) {
    const now = Date.now()
    for (const f of features) {
      const p = f.properties
      const remaining = ACTIVE_MS - p.ageSec * 1000
      if (remaining <= 0 || f.geometry.type !== 'LineString') continue
      const coords = f.geometry.coordinates
      const snr =
        p.lastSnr ?? (p.lastForward ? p.snrMedianAB : p.snrMedianBA) ?? p.snrQuality ?? p.snrMedian
      active.set(p.linkId, {
        expiresAt: now + remaining,
        feature: {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: p.lastForward ? coords : [...coords].reverse() },
          properties: {
            linkId: p.linkId,
            laneId: laneId(p.linkId, p.lastForward),
            lanes: p.kind !== 'topology',
            emphasized: isAsymmetric(p, opts.asymThreshold.value),
            functional: isFunctional(p, opts.thresholds.value),
            weight: p.weight,
            ...(snr !== undefined && { snrActive: snr }),
          },
        },
      })
    }
    // A link filtered out or scrolled off the map stops animating with it. A
    // trace leg may join nodes with no link yet: it runs its course.
    for (const [id, a] of active) if (!present.has(id) && !a.local) active.delete(id)
    syncActive()
  }

  /** Pushes the active set to the map and hides the static line under each one. */
  function syncActive() {
    if (!map || !ready) return
    const src = map.getSource(ACTIVE_SOURCE)
    if (src && 'setData' in src) {
      ;(src as maplibregl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [...active.values()].map((a) => a.feature),
      } as never)
    }
    applyFilters()
    if (active.size > 0 && raf === 0) raf = requestAnimationFrame(tick)
  }

  /**
   * Switches between the quality view (one line per link, coloured by the
   * weaker direction) and the asymmetry view (one lane per direction).
   */
  function applyView() {
    if (!map || !ready) return
    const m = map
    const asym = opts.mode.value === 'asymmetry'
    const functional = opts.mode.value === 'functional'
    const only = asym && opts.asymOnly.value
    const show = (id: string, on: boolean) => m.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none')
    m.setFilter(LAYER_NODE_WARN, issueFilter(opts.issues.value))
    const clockMax = opts.clockMaxSec.value
    m.setFilter(LAYER_NODE_CLOCK, clockMax === null ? ['boolean', false] : clockFilter(clockMax))
    show(LAYER_MEASURED, !asym)
    if (asym) syncLanes()
    show(LAYER_LANES, asym)
    show(LAYER_LANES_MISSING, asym)
    show(LAYER_LANE_ARROWS, asym)
    show(LAYER_FOCUS_LABELS, asym)
    // Topology links have no SNR, so they can never be shown as working.
    show(LAYER_TOPOLOGY, !only && !functional)
    const dark = opts.dark.value
    const thresholds = opts.thresholds.value
    const palette = opts.palette.value
    const mono = dark ? FUNCTIONAL_DARK : FUNCTIONAL_LIGHT
    m.setPaintProperty(LAYER_MEASURED, 'line-color', functional ? mono : measuredColor(dark, thresholds, palette))
    m.setPaintProperty(LAYER_ACTIVE, 'line-color', functional ? mono : activeColor(dark, thresholds, palette))
    m.setPaintProperty(
      LAYER_CASING,
      'line-width',
      asym ? laneAware(LINK_HAS_LANES, LANE_CASING_AT, 3) : widthExpr(3),
    )
    m.setPaintProperty(
      LAYER_SELECTED,
      'line-width',
      asym ? laneAware(LINK_HAS_LANES, LANE_SELECTED_AT, 2.5) : widthExpr(2.5),
    )
    m.setPaintProperty(
      LAYER_ACTIVE,
      'line-width',
      asym ? laneAware(ACTIVE_HAS_LANES, LANE_WIDTH_AT, 1) : widthExpr(1),
    )
    m.setPaintProperty(
      LAYER_ACTIVE,
      'line-offset',
      asym ? laneAware(ACTIVE_HAS_LANES, LANE_OFFSET_AT, 0, 0) : 0,
    )
    applyFilters()
  }

  /**
   * One place composes every filter: link kind, the lines hidden under an
   * animation, the "asymmetric only" restriction and the "Fonctionnel" view.
   */
  function applyFilters() {
    if (!map || !ready) return
    const only = opts.mode.value === 'asymmetry' && opts.asymOnly.value
    const functional = opts.mode.value === 'functional'
    const works = functionalFilter(opts.thresholds.value)
    const notIn = (prop: string, ids: string[]): ExpressionSpecification => [
      '!',
      ['in', ['get', prop], ['literal', ids]],
    ]
    const activeLinks = [...active.keys()]
    const activeLanes = [...active.values()].map((a) => a.feature.properties.laneId)
    const emph: ExpressionSpecification[] = only ? [EMPHASIZED] : []
    map.setFilter(LAYER_TOPOLOGY, ['all', TOPOLOGY_FILTER, notIn('linkId', activeLinks)])
    map.setFilter(LAYER_MEASURED, [
      'all',
      MEASURED_FILTER,
      notIn('linkId', activeLinks),
      ...(functional ? [works] : []),
    ])
    map.setFilter(LAYER_LANES, ['all', ['==', ['get', 'measured'], true], notIn('laneId', activeLanes), ...emph])
    map.setFilter(LAYER_LANES_MISSING, ['all', ['==', ['get', 'measured'], false], ...emph])
    map.setFilter(LAYER_LANE_ARROWS, only ? EMPHASIZED : null)
    const viewActive = functional ? ['==', ['get', 'functional'], true] : only ? EMPHASIZED : null
    map.setFilter(
      LAYER_ACTIVE,
      viewActive ? (['any', ['==', ['get', 'trace'], true], viewActive] as ExpressionSpecification) : null,
    )
    map.setFilter(
      LAYER_CASING,
      functional ? works : only ? ['in', ['get', 'linkId'], ['literal', emphasizedIds]] : null,
    )
    // The selection halo follows the view too: a link the view hides must not
    // come back as an outline because it happens to be selected.
    map.setFilter(LAYER_SELECTED, [
      'all',
      ['==', ['get', 'linkId'], selectedLink ?? '__none__'],
      ...(functional ? [works] : []),
    ])
  }

  function tick(t: number) {
    raf = 0
    if (!map || !ready) return
    const now = Date.now()
    let expired = false
    for (const [id, a] of active) {
      if (a.expiresAt <= now) {
        active.delete(id)
        expired = true
      }
    }
    if (expired) syncActive()
    if (active.size === 0) return
    if (!reducedMotion && t - lastDashAt >= DASH_FRAME_MS) {
      dashFrame = (dashFrame + 1) % DASH_FRAMES.length
      lastDashAt = t
      map.setPaintProperty(LAYER_ACTIVE, 'line-dasharray', DASH_FRAMES[dashFrame])
    }
    if (raf === 0) raf = requestAnimationFrame(tick)
  }

  function setNodes(fc: FeatureCollection<NodeProperties>) {
    pendingNodes = fc
    if (!map || !ready) return
    const src = map.getSource(NODES_SOURCE)
    if (src && 'setData' in src) {
      ;(src as maplibregl.GeoJSONSource).setData(fc as never)
    }
  }

  function highlight(linkId: string | null) {
    selectedLink = linkId
    computeFocus()
    updateFocusLabels()
    if (!map || !map.getLayer(LAYER_SELECTED)) return
    applyFilters()
    applyFocus()
  }

  function highlightNode(key: string | null) {
    selectedNode = key
    computeFocus()
    if (!map || !map.getLayer(LAYER_NODE_SELECTED)) return
    map.setFilter(LAYER_NODE_SELECTED, ['==', ['get', 'key'], key ?? '__none__'])
    applyFocus()
  }

  /**
   * What stays opaque: a selected link and its two ends, or a selected node with
   * every live link it has and the neighbours at their other end.
   */
  function computeFocus() {
    focusLinks = []
    focusNodes = []
    if (selectedLink) {
      const p = findLink(selectedLink)?.properties
      focusLinks = [selectedLink]
      focusNodes = p ? [p.aKey, p.bKey] : []
    } else if (selectedNode) {
      const key = selectedNode
      focusNodes = [key]
      for (const f of pendingLinks?.features ?? []) {
        const { aKey, bKey, linkId } = f.properties
        if (aKey !== key && bKey !== key) continue
        focusLinks.push(linkId)
        focusNodes.push(aKey === key ? bKey : aKey)
      }
    }
  }

  /**
   * Two labels for the selected link, "12.5 dB · -96 dBm" (or "non mesuré"),
   * coloured like the sender in the panel's direction tiles: A → B on one side
   * of the link, B → A on the other. Directions known only from TRACE packets
   * have no RSSI: only an observer's radio reports one.
   */
  function updateFocusLabels() {
    if (!map || !ready) return
    const src = map.getSource(FOCUS_LABELS_SOURCE)
    if (!src || !('setData' in src)) return
    const f = selectedLink ? findLink(selectedLink) : undefined
    const features: Feature<FocusLabel, Point>[] = []
    if (f && f.geometry.type === 'LineString') {
      const p = f.properties
      const dark = opts.dark.value
      const reading = (snr?: number, count?: number, rssi?: number) =>
        snr === undefined || !count
          ? 'non mesuré'
          : `${snr.toFixed(1)} dB${rssi === undefined ? '' : ` · ${Math.round(rssi)} dBm`}`
      const [pa, pb] = f.geometry.coordinates as [[number, number], [number, number]]
      const mid = [(pa[0] + pb[0]) / 2, (pa[1] + pb[1]) / 2]
      features.push(
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: mid },
          properties: {
            text: reading(p.snrMedianAB, p.snrCountAB, p.rssiMeanAB),
            color: dark ? NODE_A_DARK : NODE_A_LIGHT,
            ...directionSide(pa, pb),
          },
        },
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: mid },
          properties: {
            text: reading(p.snrMedianBA, p.snrCountBA, p.rssiMeanBA),
            color: dark ? NODE_B_DARK : NODE_B_LIGHT,
            ...directionSide(pb, pa),
          },
        },
      )
    }
    ;(src as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features } as never)
  }

  function findLink(linkId: string) {
    return pendingLinks?.features.find((f) => f.properties.linkId === linkId)
  }

  /** Fades everything outside the focus (see computeFocus); restores it all without one. */
  function applyFocus() {
    if (!map || !ready) return
    const on = selectedLink !== null || selectedNode !== null || traceFocus !== null
    const links = traceFocus?.links ?? focusLinks
    const nodes = traceFocus?.nodes ?? focusNodes
    const inLinks: ExpressionSpecification = ['in', ['get', 'linkId'], ['literal', links]]
    for (const { layer, prop, base } of LINK_OPACITY) {
      // The animated packet line is fresh by definition: no age fade.
      const dim = opts.bridge.value ? BRIDGE_LINK_DIM : 1
      const rest = layer === LAYER_ACTIVE ? base * dim : measureFade(base * dim, opts.measureRetentionSec.value)
      map.setPaintProperty(layer, prop, on ? ['case', inLinks, 1, FOCUS_DIM] : rest)
    }
    const inNodes: ExpressionSpecification = ['in', ['get', 'key'], ['literal', nodes]]
    const nodeOpacity = on ? ['case', inNodes, 1, NODE_FOCUS_DIM] : 1
    map.setPaintProperty(LAYER_NODES, 'circle-opacity', nodeOpacity)
    map.setPaintProperty(LAYER_NODES, 'circle-stroke-opacity', nodeOpacity)
    map.setPaintProperty(LAYER_LISTEN, 'icon-opacity', nodeOpacity)
    map.setPaintProperty(LAYER_COMPANIONS, 'icon-opacity', nodeOpacity)
    map.setPaintProperty(LAYER_NODE_WARN, 'icon-opacity', nodeOpacity)
    map.setPaintProperty(LAYER_NODE_CLOCK, 'icon-opacity', nodeOpacity)
    // Only focused nodes keep a label: other labels would compete for the space.
    map.setFilter(LAYER_NODE_LABELS, on ? inNodes : null)
  }

  /**
   * Frames the link so it fills the part of the map left visible by the
   * overlays, given as padding in pixels.
   */
  function focusLink(linkId: string, padding: Padding) {
    const f = findLink(linkId)
    if (!f || f.geometry.type !== 'LineString') return
    focusPoints(f.geometry.coordinates as [number, number][], padding)
  }

  /** Frames a set of [lng, lat] points the same way (a node and its neighbours). */
  function focusPoints(points: [number, number][], padding: Padding) {
    if (!map || points.length === 0) return
    const lngs = points.map((p) => p[0])
    const lats = points.map((p) => p[1])
    // fitBounds refuses padding wider than the map; shrink it proportionally.
    const { clientWidth: w, clientHeight: h } = map.getContainer()
    const sx = Math.min(1, (w - 80) / (padding.left + padding.right))
    const sy = Math.min(1, (h - 80) / (padding.top + padding.bottom))
    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      {
        padding: {
          top: padding.top * sy,
          bottom: padding.bottom * sy,
          left: padding.left * sx,
          right: padding.right * sx,
        },
        maxZoom: 15,
        duration: 600,
      },
    )
  }

  /** Re-theme in place: dark mode gets its own steps, not an automatic flip. */
  function retheme() {
    if (!map || !ready) return
    for (const id of [
      LAYER_SITE_NUMBERS,
      LAYER_SITE_RING,
      LAYER_SITE_LINKS,
      LAYER_TRACE_LABELS,
      LAYER_TRACE_NUMBERS,
      LAYER_TRACE_STOPS,
      LAYER_TRACE_LINE,
      LAYER_PROFILE_MARK,
      LAYER_FOCUS_LABELS,
      LAYER_NODE_LABELS,
      LAYER_NODE_SELECTED,
      LAYER_NODE_CLOCK,
      LAYER_NODE_WARN,
      LAYER_COMPANIONS,
      LAYER_LISTEN,
      LAYER_NODES,
      LAYER_SELECTED,
      LAYER_ACTIVE,
      LAYER_LANE_ARROWS,
      LAYER_LANES,
      LAYER_LANES_MISSING,
      LAYER_MEASURED,
      LAYER_TOPOLOGY,
      LAYER_CASING,
    ]) {
      if (map.getLayer(id)) map.removeLayer(id)
    }
    const dark = opts.dark.value
    map.setPaintProperty('basemap', 'raster-opacity', dark ? 0.45 : 0.75)
    map.setPaintProperty('basemap', 'raster-brightness-max', dark ? 0.55 : 1)
    map.setPaintProperty(LAYER_ALTITUDE, 'color-relief-opacity', altitudeOpacity(dark))
    addDataLayers()
    applyView()
    applyBridge() // also re-applies the focus
    updateFocusLabels()
    setTraceLabels(traceHops) // colours follow the theme
    syncActive()
  }

  /** Shows or hides the altitude layer; showing it stretches the ramp to the view. */
  function applyAltitude() {
    if (!map || !ready) return
    const on = opts.altitude.value
    map.setLayoutProperty(LAYER_ALTITUDE, 'visibility', on ? 'visible' : 'none')
    if (on) scheduleAltitudeStretch(0)
    else {
      window.clearTimeout(stretchTimer)
      stretchSeq++
      opts.onAltitudeRange?.(null)
    }
  }

  let stretchTimer = 0
  let stretchSeq = 0
  /**
   * Re-spreads the colours over the altitudes in view, once the map settles.
   * A later move wins over a stretch still downloading tiles.
   */
  function scheduleAltitudeStretch(delay = 250) {
    window.clearTimeout(stretchTimer)
    stretchTimer = window.setTimeout(async () => {
      if (!map) return
      const seq = ++stretchSeq
      const range = await viewRange(bboxOf(map), map.getZoom())
      if (!map || seq !== stretchSeq || !range || !opts.altitude.value) return
      map.setPaintProperty(LAYER_ALTITUDE, 'color-relief-color', reliefColorExpression(range) as never)
      opts.onAltitudeRange?.(range)
    }, delay)
  }

  /**
   * Marks the point a fraction of the way from A to B along a link (the
   * line-of-sight chart under the pointer); null clears it. A straight line in
   * lng/lat is close enough to the geodesic over a radio hop.
   */
  function markOnLink(linkId: string | null, fraction: number | null) {
    const src = map?.getSource(PROFILE_MARK_SOURCE) as maplibregl.GeoJSONSource | undefined
    if (!src) return
    const g = linkId && fraction !== null ? findLink(linkId)?.geometry : undefined
    const coords = g?.type === 'LineString' ? g.coordinates : undefined
    if (!coords || coords.length < 2) {
      src.setData(EMPTY_FC as never)
      return
    }
    const [lngA, latA] = coords[0]!
    const [lngB, latB] = coords[coords.length - 1]!
    const f = Math.min(1, Math.max(0, fraction!))
    src.setData({
      type: 'Feature',
      properties: {},
      geometry: { type: 'Point', coordinates: [lngA! + (lngB! - lngA!) * f, latA! + (latB! - latA!) * f] },
    } as never)
  }

  function nodeCoords(key: string): [number, number] | undefined {
    const g = pendingNodes?.features.find((f) => f.properties.key === key)?.geometry
    return g?.type === 'Point' ? (g.coordinates as [number, number]) : undefined
  }

  /**
   * Animates trace legs with the live-packet animation, each in the direction
   * it travelled, coloured by the SNR measured at its far end when known.
   */
  function animateHops(hops: { from: string; to: string; snr?: number }[]) {
    const now = Date.now()
    for (const h of hops) {
      const a = nodeCoords(h.from)
      const b = nodeCoords(h.to)
      if (!a || !b || h.from === h.to) continue
      const forward = h.from <= h.to
      const linkId = forward ? `${h.from}:${h.to}` : `${h.to}:${h.from}`
      const p = findLink(linkId)?.properties
      const snr = h.snr ?? (p && (forward ? p.snrMedianAB : p.snrMedianBA))
      active.set(linkId, {
        expiresAt: now + ACTIVE_MS,
        local: true,
        feature: {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [a, b] },
          properties: {
            linkId,
            laneId: laneId(linkId, forward),
            lanes: !!p && p.kind !== 'topology',
            emphasized: !!p && isAsymmetric(p, opts.asymThreshold.value),
            functional: !!p && isFunctional(p, opts.thresholds.value),
            weight: p?.weight ?? 1,
            trace: true,
            ...(snr !== undefined && { snrActive: snr }),
          },
        },
      })
    }
    syncActive()
  }

  /**
   * Focuses the map on traces: the nodes each route visits, in order, and the
   * links between consecutive ones keep full strength; everything else fades
   * as for a selected link. null lifts it.
   */
  function setTraceFocus(routes: string[][] | null) {
    traceFocus = routes
      ? {
          nodes: [...new Set(routes.flat())],
          links: [
            ...new Set(
              routes.flatMap((r) =>
                r.slice(1).map((to, i) => {
                  const from = r[i]!
                  return from <= to ? `${from}:${to}` : `${to}:${from}`
                }),
              ),
            ),
          ],
        }
      : null
    applyFocus()
  }

  let traceHops: { from: string; to: string; snr?: number }[] = []

  /**
   * Labels every link of a returned trace with the SNR measured each way, as
   * if each were the selected link: the A → B reading on one side in A's
   * colour, B → A on the other in B's. An empty list clears them.
   */
  function setTraceLabels(hops: { from: string; to: string; snr?: number }[]) {
    traceHops = hops
    const src = map?.getSource(TRACE_LABELS_SOURCE) as maplibregl.GeoJSONSource | undefined
    if (!src) return
    const dark = opts.dark.value
    const features: Feature<FocusLabel, Point>[] = []
    for (const h of hops) {
      if (h.snr === undefined || h.from === h.to) continue
      const forward = h.from <= h.to
      const a = nodeCoords(forward ? h.from : h.to)
      const b = nodeCoords(forward ? h.to : h.from)
      if (!a || !b) continue
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] },
        properties: {
          text: `${h.snr.toFixed(1)} dB`,
          color: forward ? (dark ? NODE_A_DARK : NODE_A_LIGHT) : dark ? NODE_B_DARK : NODE_B_LIGHT,
          ...(forward ? directionSide(a, b) : directionSide(b, a)),
        },
      })
    }
    src.setData({ type: 'FeatureCollection', features } as never)
  }

  /** Draws the path being built for a trace: a dashed line and numbered stops. */
  function setTracePath(start: string | null, keys: string[]) {
    const src = map?.getSource(TRACE_PATH_SOURCE) as maplibregl.GeoJSONSource | undefined
    if (!src) return
    const stops = keys.map((k) => nodeCoords(k))
    const line = [start ? nodeCoords(start) : undefined, ...stops].filter((c) => c !== undefined)
    const features: unknown[] = []
    if (line.length >= 2) {
      features.push({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: line } })
    }
    // One badge per node, listing every step it is on: "1|3" out and back.
    const steps = new Map<string, number[]>()
    keys.forEach((k, i) => steps.set(k, [...(steps.get(k) ?? []), i + 1]))
    for (const [k, idx] of steps) {
      const c = nodeCoords(k)
      if (c) features.push({ type: 'Feature', properties: { n: idx.join('|') }, geometry: { type: 'Point', coordinates: c } })
    }
    src.setData({ type: 'FeatureCollection', features } as never)
  }

  /** Proposed sites and their predicted links (siting/plan sitesGeoJSON). */
  function setSites(fc: { type: 'FeatureCollection'; features: unknown[] }) {
    const src = map?.getSource(SITING_SOURCE) as maplibregl.GeoJSONSource | undefined
    if (src) src.setData(fc as never)
    else pendingSites = fc
  }

  /** Shows or hides the "Rôle de pont" layer: node size and fill by level. */
  function applyBridge() {
    if (!map || !ready) return
    const on = opts.bridge.value
    map.setPaintProperty(LAYER_NODES, 'circle-radius', on ? bridgeRadiusExpression() : NODE_RADIUS)
    map.setPaintProperty(
      LAYER_NODES,
      'circle-color',
      on ? bridgeFillExpression(opts.dark.value) : opts.dark.value ? SURFACE_DARK : SURFACE_LIGHT,
    )
    applyFocus()
  }

  function fitTo(bbox: BBox) {
    map?.fitBounds(
      [
        [bbox.minLng, bbox.minLat],
        [bbox.maxLng, bbox.maxLat],
      ],
      { padding: 48, duration: 400 },
    )
  }

  onScopeDispose(() => {
    window.clearTimeout(stretchTimer)
    if (raf !== 0) cancelAnimationFrame(raf)
    map?.remove()
    map = null
    ready = false
  })

  return {
    mount,
    setLinks,
    setNodes,
    highlight,
    highlightNode,
    retheme,
    fitTo,
    applyView,
    applyAltitude,
    applyBridge,
    applyFocus,
    markOnLink,
    animateHops,
    setTracePath,
    setTraceLabels,
    setTraceFocus,
    setSites,
    focusLink,
    focusPoints,
  }
}
